import { api } from "./http-client";
import { User } from "../interfaces/user.interface";
import { LocationOption } from "../interfaces/charter-destination.interface";
import { PageDto, PageOptionsDto } from "../types/pagination";
import {
  CreateUserDto,
  UpdateUserDto,
  CreateUserWithDocumentsDto,
  UserDocumentFiles,
} from "../dtos/user.dto";

/** Mirrors ReferredUserRow in Backend/api — one referred signup. */
export interface ReferralRow {
  referredById: string;
  userName: string;
  userEmail: string;
  referrerName: string;
  referrerEmail: string;
  referrerUsername: string | null;
  matchedBy: "EMAIL" | "USERNAME";
  createdAt: string;
}

export const usersService = {
  list: async (
    params?: PageOptionsDto & { role?: string; isActive?: boolean; accountStatus?: string; usedOtpFallback?: boolean },
  ): Promise<PageDto<User>> => {
    const response = await api.request<PageDto<User>>({
      url: "/users",
      method: "GET",
      params,
    });
    return response.data;
  },
  create: async (data: CreateUserDto): Promise<User> => {
    const response = await api.request<User>({
      url: "/users",
      method: "POST",
      data,
    });
    return response.data;
  },
  createWithDocuments: async (
    data: CreateUserWithDocumentsDto,
    files: UserDocumentFiles,
  ): Promise<User> => {
    const form = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        form.append(key, String(value));
      }
    });
    Object.entries(files).forEach(([key, file]) => {
      if (file) form.append(key, file);
    });

    const response = await api.request<User>({
      url: "/users/with-documents",
      method: "POST",
      data: form,
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
  // Edit-mode gap fill: only documents the user never uploaded. The backend
  // rejects a field that already holds a file, so nothing is overwritten.
  uploadDocuments: async (id: string, files: UserDocumentFiles): Promise<User> => {
    const form = new FormData();
    Object.entries(files).forEach(([key, file]) => {
      if (file) form.append(key, file);
    });

    const response = await api.request<User>({
      url: `/users/${id}/documents`,
      method: "POST",
      data: form,
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
  update: async (id: string, data: UpdateUserDto): Promise<User> => {
    const response = await api.request<User>({
      url: `/users/${id}`,
      method: "PATCH",
      data,
    });
    return response.data;
  },
  listLocations: async (): Promise<LocationOption[]> => {
    const response = await api.request<LocationOption[]>({
      url: "/users/locations",
      method: "GET",
    });
    return response.data;
  },
  listReferrals: async (): Promise<ReferralRow[]> => {
    const response = await api.request<ReferralRow[]>({
      url: "/users/referrals",
      method: "GET",
    });
    return response.data;
  },
  listDeletionRequests: async (): Promise<User[]> => {
    const response = await api.request<User[]>({
      url: "/users/deletion-requests",
      method: "GET",
    });
    return response.data;
  },
  requestDeletion: async (
    id: string,
    data: { companyAction?: "transfer" | "delete"; newOwnerId?: string; reason?: string },
  ): Promise<{ scheduledAt: string; expiresAt: string }> => {
    const response = await api.request<{ scheduledAt: string; expiresAt: string }>({
      url: `/users/${id}/request-deletion`,
      method: "POST",
      data,
    });
    return response.data;
  },
  getTransferCandidates: async (
    id: string,
  ): Promise<{ id: string; fullName: string; email: string }[]> => {
    const response = await api.request<{ id: string; fullName: string; email: string }[]>({
      url: `/users/${id}/transfer-candidates`,
      method: "GET",
    });
    return response.data;
  },
  confirmDeletion: async (id: string): Promise<{ message: string }> => {
    const response = await api.request<{ message: string }>({
      url: `/users/${id}/confirm-deletion`,
      method: "POST",
    });
    return response.data;
  },
  cancelDeletion: async (id: string): Promise<{ message: string }> => {
    const response = await api.request<{ message: string }>({
      url: `/users/${id}/cancel-deletion`,
      method: "POST",
    });
    return response.data;
  },
  verifyId: async (id: string): Promise<{ success: boolean }> => {
    const response = await api.request<{ success: boolean }>({
      url: `/admin/users/${id}/verify-id`,
      method: "POST",
    });
    return response.data;
  },
  unverifyId: async (id: string): Promise<{ success: boolean }> => {
    const response = await api.request<{ success: boolean }>({
      url: `/admin/users/${id}/unverify-id`,
      method: "POST",
    });
    return response.data;
  },
  getDocumentPassword: async (id: string, masterKey: string): Promise<{ password: string | null }> => {
    const response = await api.request<{ password: string | null }>({
      url: `/users/${id}/document-password`,
      method: "POST",
      data: { masterKey },
    });
    return response.data;
  },
  downloadIdDocument: async (id: string, filePassword: string): Promise<Blob> => {
    const response = await api.request<Blob>({
      url: `/users/${id}/id-document`,
      method: "POST",
      data: { filePassword },
      responseType: "blob",
    });
    return response.data;
  },
  downloadLicenseDocument: async (id: string): Promise<Blob> => {
    const response = await api.request<Blob>({
      url: `/users/${id}/license-document`,
      method: "GET",
      responseType: "blob",
    });
    return response.data;
  },
  downloadCredentialsDocument: async (id: string): Promise<Blob> => {
    const response = await api.request<Blob>({
      url: `/users/${id}/credentials-document`,
      method: "GET",
      responseType: "blob",
    });
    return response.data;
  },
};
